import { Box, Stack, Typography, useMediaQuery } from "@mui/material";
import React, { useEffect, useState } from "react";
import { colors } from "../../theme/theme";
import ImageSlider from "../../Components/Product/ImageSlider";
import CustomAccordion from "../../Components/Common/CustomAccordion";
import Footer from "../../Components/Common/Footer";
import { useLocation, useSearchParams } from "react-router-dom";
import SizeSelector from "../../Components/Product/SizeSelector";
import { logUserAction } from "../../utils/analytics";

const ProductPage = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Get search query from location.state first, then URL params, then empty string
  const searchQuery =
    location.state?.searchQuery || searchParams.get("q") || "";
  const product = location.state?.product || {};

  const [openIndexes, setOpenIndexes] = useState([0, 1]);

  const toggleAccordion = (index) => {
    setOpenIndexes((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index],
    );
  };

  const smallScreen = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "instant",
    });
  }, []);

  return (
    <Box sx={{ backgroundColor: "#FFFFFF", minHeight: "100vh" }}>
      <Stack
        direction={smallScreen ? "column" : "row"}
        sx={{
          maxWidth: "1400px",
          mx: "auto",
          alignItems: "stretch",
          pt: smallScreen ? 0 : 3,
          px: smallScreen ? 0 : 3,
          gap: smallScreen ? 0 : 3,
        }}
      >
        <ImageSlider
          productImages={
            product?.images?.length > 0
              ? product?.images
              : [product?.image_url] || []
          }
          searchQuery={searchQuery}
        />
        <Box
          sx={{
            p: smallScreen ? 3 : 4,
            width: smallScreen ? "100%" : "50%",
            backgroundColor: "#fff",
            position: "relative",
            borderRadius: smallScreen ? 0 : "12px",
            boxShadow: smallScreen ? "none" : "0 1px 3px rgba(0,0,0,0.1)",
            border: smallScreen ? "none" : "1px solid #eee",
          }}
        >
          {product?.price?.sale_percentage != null && (
            <Box
              sx={{
                position: "absolute",
                top: smallScreen ? "16px" : "24px",
                right: smallScreen ? "16px" : "24px",
                backgroundColor: "#FF3B30",
                color: "#fff",
                borderRadius: "4px",
                padding: "4px 8px",
                fontSize: "12px",
                fontFamily: "Poppins-Bold",
                letterSpacing: "0.5px",
                fontWeight: 700,
                zIndex: 2,
                boxShadow: "0 2px 8px rgba(255,59,48,0.3)"
              }}
            >
              SALE -{Math.round(product?.price?.sale_percentage)}%
            </Box>
          )}

          <Box>
            {/* Brand */}
            <Typography
              sx={{
                fontSize: "12px",
                fontFamily: "Poppins-Medium",
                fontWeight: 500,
                color: "#999",
                textTransform: "uppercase",
                mb: 1.5,
              }}
            >
              {product?.brand}
            </Typography>

            <Typography
              sx={{
                color: "#1a1a1a",
                fontFamily: "'Lora', serif",
                fontWeight: 600,
                fontSize: smallScreen ? "24px" : "32px",
                lineHeight: 1.2,
                letterSpacing: "-0.5px",
                mb: 1,
              }}
            >
              {product?.title}
            </Typography>

            {/* Price Section */}
            <Box sx={{ mt: 2, mb: smallScreen ? 1.5 : 3 }}>
              <Stack direction="row" alignItems="baseline" spacing={1.5}>
                <Typography
                  sx={{
                    fontSize: smallScreen ? "28px" : "32px",
                    fontWeight: 800,
                    color: "#1a1a1a",
                    fontFamily: "Poppins-Bold",
                  }}
                >
                  {product?.price?.discounted || product?.price?.original}
                  <Typography
                    component="span"
                    sx={{
                      fontSize: smallScreen ? "18px" : "20px",
                      fontWeight: 600,
                      color: "#666",
                      ml: 0.5,
                    }}
                  >
                    {product?.price?.currency}
                  </Typography>
                </Typography>
                {product?.price?.discounted && product?.price?.original !== product?.price?.discounted && (
                  <Typography
                    sx={{
                      fontSize: smallScreen ? "18px" : "20px",
                      color: "#999",
                      fontFamily: "Poppins-Regular",
                      textDecoration: "line-through",
                    }}
                  >
                    {product?.price?.original}
                  </Typography>
                )}
              </Stack>
            </Box>
          </Box>

          {/* Color Selection */}
          {/* <ColorSelector /> */}

          {/* Size Selector */}
          {product?.variants?.length > 0 && (
            <Box sx={{ mt: smallScreen ? 2 : 3, mb: smallScreen ? 3 : 4 }}>
              <Typography
                sx={{
                  fontFamily: "Poppins-SemiBold",
                  fontWeight: 600,
                  color: "#1a1a1a",
                  fontSize: "13px",
                  mb: 1.5,
                }}
              >
                Available Sizes
              </Typography>
              <SizeSelector sizes={product?.variants} />
            </Box>
          )}

          {/* CTA Button */}
          <Box
            sx={{
              width: "100%",
              background: "linear-gradient(135deg, #FFB79C 0%, #FFA785 100%)",
              boxShadow: "0 4px 14px rgba(255,183,156,0.4)",
              my: 3,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              borderRadius: "8px",
              py: 2,
              cursor: "pointer",
              transition: "all 0.2s ease",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: "0 6px 20px rgba(255,183,156,0.6)",
              },
              "&:active": {
                transform: "translateY(0px)",
                boxShadow: "0 2px 8px rgba(255,183,156,0.4)",
              },
            }}
            onClick={() => {
              if (product?.url) {
                window.open(product.url, "_blank");
              }

              // Run logging without blocking popup
              logUserAction({
                action: "checkout_redirect",
                data: {
                  product_id: product?.product_id || "",
                  product_title: product?.title || "",
                  brand: product?.brand || "",
                  redirect_url: product?.url || null,
                },
                timestamp: new Date().toISOString(),
              });
            }}
          >
            <Typography
              sx={{
                fontFamily: "Poppins-SemiBold",
                fontWeight: 600,
                color: "#fff",
                fontSize: "15px",
              }}
            >
              Buy on {product?.brand}
            </Typography>
          </Box>

          {/* Product Details Section */}
          {product?.description && product.description !== "N/A" && (
            <Box sx={{ mt: 4, pt: 3, borderTop: "1px solid rgba(0,0,0,0.06)" }}>
              <Typography
                sx={{
                  fontFamily: "Poppins-SemiBold",
                  fontWeight: 600,
                  fontSize: "16px",
                  color: "#1a1a1a",
                  mb: 1.5,
                }}
              >
                Product Description
              </Typography>
              <Typography
                sx={{
                  fontFamily: "Poppins-Regular",
                  fontSize: "14px",
                  color: "#666",
                  lineHeight: 1.6,
                  whiteSpace: "pre-wrap",
                }}
              >
                {product?.description}
              </Typography>
            </Box>
          )}
        </Box>
      </Stack>
      <Footer />
    </Box>
  );
};

export default ProductPage;
